export interface BudgetCustomerSources {
  /** `saleOrder.customerCompany` */
  saleOrderCompany?: any;
  /**
   * El partner de la cotización. En el componente vive en `this.partner`, que se rellena
   * desde `saleOrder.partner` o, si no viene embebido, pidiéndolo por `saleOrder.partnerId`.
   */
  saleOrderContact?: any;
  /** `opportunity.customerCompany` */
  opportunityCompany?: any;
  /** `opportunity.contact` — el contacto explícito de la oportunidad */
  opportunityContact?: any;
  /** `opportunity.partner` — respaldo cuando la oportunidad no declara contacto */
  opportunityPartner?: any;
}

export interface BudgetCustomer {
  company: any;
  contact: any;
  /**
   * Las mismas fuentes, en el mismo orden de precedencia. De aquí salen la dirección y el
   * NIF, así que van ordenadas igual que el contacto: si divergieran, el documento nombraría
   * a una persona y pondría la dirección de otra.
   */
  contactSources: any[];
}

/**
 * CEC-638. De qué registro salen la empresa y el contacto del bloque de cliente del PDF.
 *
 * **Manda la cotización; si no trae el dato, se cae a la oportunidad** (opción C, decidida por
 * la PO el 27/08/2026):
 *
 * ```
 * contacto:  cotización → oportunidad.contact → oportunidad.partner
 * empresa:   cotización → oportunidad
 * ```
 *
 * El defecto que arregla: el usuario editaba el contacto en la cotización, la pantalla se lo
 * mostraba cambiado, y el PDF seguía imprimiendo el de la oportunidad — se enteraba cuando el
 * documento ya estaba fuera.
 *
 * **Por qué no «solo la cotización»:** dejaría en blanco los documentos cuya cotización no
 * tiene contacto propio y hoy se apoyan en el de la oportunidad. Medido en DEV: con esta
 * cadena **ninguno se vacía**.
 *
 * **Por qué las dos mitades cambian juntas:** si solo se invirtiera el contacto, una misma
 * línea saldría de dos registros distintos. Medido antes de tocarla: **cero** cotizaciones
 * tienen empresa propia Y su oportunidad otra distinta, así que invertirla no mueve ningún
 * documento.
 *
 * No decide **cómo** se pinta la línea —eso es `buildBudgetCustomerNameLine`, de CEC-617— ni
 * cómo se compone la dirección, que CEC-316 acota aparte.
 */
export function resolveBudgetCustomer({
  saleOrderCompany,
  saleOrderContact,
  opportunityCompany,
  opportunityContact,
  opportunityPartner,
}: BudgetCustomerSources): BudgetCustomer {
  const opportunitySource = opportunityContact || opportunityPartner || null;
  const contactSources = [saleOrderContact || null, opportunitySource].filter(Boolean);

  return {
    company: saleOrderCompany || opportunityCompany || null,
    contact: contactSources[0] || null,
    contactSources,
  };
}
